import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type BackHandler = () => void;

interface PageBackValue {
  /** The current page's back action, or null when the page has none. */
  back: BackHandler | null;
  /** Registers a handler; returns an unregister function. */
  register: (handler: BackHandler) => () => void;
}

const PageBackContext = createContext<PageBackValue | null>(null);

/**
 * Lets the app header show a back arrow for whatever page is on screen.
 *
 * Pages already declare "I am a sub-page" by passing `onBack` to PageHeader,
 * so rather than inventing a second per-page opt-in, PageHeader publishes that
 * same handler here and the header picks it up. Any page that already has an
 * in-body back button gets the header affordance with no change of its own.
 */
export function PageBackProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [back, setBack] = useState<BackHandler | null>(null);
  // A STACK, not a single slot. Routes swap by mounting the incoming page
  // before unmounting the outgoing one, and a page can hold two registrants
  // at once (PageHeader plus a detail header). With one slot, whichever
  // unmounted last cleared the handler even when another was still mounted,
  // and the header silently lost its back arrow. The top of the stack is the
  // most recent registrant; popping falls back to the one beneath it.
  const stackRef = useRef<BackHandler[]>([]);

  const sync = useCallback(() => {
    const top = stackRef.current[stackRef.current.length - 1] ?? null;
    // Stored in a closure: useState treats a bare function as an updater.
    setBack(() => top);
  }, []);

  const register = useCallback(
    (handler: BackHandler) => {
      stackRef.current.push(handler);
      sync();
      return () => {
        const i = stackRef.current.lastIndexOf(handler);
        if (i === -1) return;
        stackRef.current.splice(i, 1);
        sync();
      };
    },
    [sync],
  );

  const value = useMemo(() => ({ back, register }), [back, register]);

  return (
    <PageBackContext.Provider value={value}>{children}</PageBackContext.Provider>
  );
}

/** Read the active page's back action. Used by the app header. */
export function usePageBackAction(): BackHandler | null {
  return useContext(PageBackContext)?.back ?? null;
}

/**
 * Publish this page's back action to the header.
 *
 * Returns whether a provider actually took it. Callers use that to decide
 * whether they still need their own back button: auth and onboarding routes
 * are mounted OUTSIDE AppLayout in App.tsx, so there is no provider and no app
 * header to host the arrow. Without this signal, hiding the in-body button on
 * mobile left those routes with no back affordance at all.
 */
export function usePageBack(
  handler: BackHandler | null | undefined,
): boolean {
  const ctx = useContext(PageBackContext);
  const register = ctx?.register;

  // Kept in a ref so a page passing an inline arrow doesn't re-register on
  // every render, which would loop.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const hasHandler = Boolean(handler);

  useEffect(() => {
    if (!register || !hasHandler) return;
    return register(() => handlerRef.current?.());
  }, [register, hasHandler]);

  return Boolean(register) && hasHandler;
}
